import { Box, Paper, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useOCRStore } from '../../store/ocrStore';
import { AsyncAutocompleteRenderer, TextCellRenderer } from '../CellRenderers';
import { CustomFieldsTable } from '../CustomFieldsTable';
import { DataTableComponent } from '../DataTableComponent';
import { RightPanelHeader } from '../RightPanelHeader';

const cellRenderers: Record<string, React.FC<any>> = {
  'text': TextCellRenderer,
  'autocomplete': AsyncAutocompleteRenderer,
};

export const RightPanelContainer: React.FC = () => {
  const tableIds = useOCRStore(state => state.tableIds);
  const tables = useOCRStore(state => state.tables);
  const deleteAnnotationAndAssociatedTables = useOCRStore(state => state.deleteAnnotationAndAssociatedTables);

  const usedSensorIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(tables).forEach(table => {
      table.grid.flat().forEach(cell => {
        if (cell && !('spanned' in cell)) {
          const sensor = cell.sensor;
          if (typeof sensor === 'object' && sensor?.sensorId) {
            ids.add(sensor.sensorId);
          }
        }
      });
    });
    return ids;
  }, [tables]);

  const handleDelete = useCallback((annotationId: string) => {
    deleteAnnotationAndAssociatedTables(annotationId);
  }, [deleteAnnotationAndAssociatedTables]);

  if (tableIds.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3
        }}
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          No extracted data yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the annotate tool to select areas from the document for OCR analysis
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Paper
        sx={{
          border: theme => `0.5px solid ${theme.palette.Neutrals[200]}`,
          height: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          px: 2,
          pb: 2,
          backgroundColor: 'background.paper'
        }}
        elevation={0}
      >
        <RightPanelHeader />

        {tableIds.map((tableId) => (
          <DataTableComponent
            key={tableId}
            tableId={tableId}
            onDelete={handleDelete}
            cellRenderers={cellRenderers}
            usedSensorIds={usedSensorIds}
          />
        ))}

        <CustomFieldsTable />
      </Paper>
    </Box>
  );
};